import type { LinkAnswer, LinkGist } from '../domain/noteTypes'
import { getSupabaseClient } from '../supabase/client'
import { getSupabaseConfig } from '../supabase/config'
import { getNativePlatform } from '../utils/nativeRuntime'
import { GIST_SETTINGS } from './gistPolicy'
import { resolveLinkMetadataApiBaseUrl } from './linkMetadata'

/** Whether this build shows gists: on in staging through VITE_LINK_GIST, or once `links.gist.enabled` ships. */
export function gistsEnabled(): boolean {
  return GIST_SETTINGS.enabled || import.meta.env.VITE_LINK_GIST === 'on'
}

export type GistFetchResult =
  | { status: 'ready'; gist: LinkGist }
  | { status: 'unreadable' | 'failed' | 'signed-out'; reason: string }

export type AnswerFetchResult =
  | { status: 'ready'; answer: Omit<LinkAnswer, 'id'> }
  | { status: 'unreadable' | 'failed' | 'signed-out'; reason: string }

interface RouteBody { readable?: boolean; status?: string; gist?: LinkGist; answer?: Omit<LinkAnswer, 'id'>; reason?: string }
type RouteOutcome = { ok: true; body: RouteBody } | { ok: false; status: 'failed' | 'signed-out'; reason: string }

/** One call to the gist route, signed in. Never throws: every outcome is a result the caller can show quietly. */
async function callRoute(payload: Record<string, unknown>, fetcher?: typeof fetch): Promise<RouteOutcome> {
  try {
    // A build without account sign-in (a local dev server) is the same as being signed out.
    if (!getSupabaseConfig()) return { ok: false, status: 'signed-out', reason: 'signed-out' }
    const client = await getSupabaseClient()
    const { data } = await client.auth.getSession()
    const token = data.session?.access_token
    if (!token) return { ok: false, status: 'signed-out', reason: 'signed-out' }
    const base = resolveLinkMetadataApiBaseUrl({
      configuredBaseUrl: import.meta.env.VITE_API_BASE_URL,
      appEnvironment: getSupabaseConfig()?.environment ?? 'development',
      nativePlatform: getNativePlatform(window),
    })
    const response = await (fetcher ?? fetch)(`${base}/api/links/gist`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (!response.ok) {
      // The server names a spent limit; the controller stops asking until it resets.
      const error = (await response.json().catch(() => null) as { error?: unknown } | null)?.error
      return { ok: false, status: 'failed', reason: response.status === 429 && typeof error === 'string' ? error : `http-${response.status}` }
    }
    return { ok: true, body: await response.json() as RouteBody }
  } catch {
    return { ok: false, status: 'failed', reason: 'network' }
  }
}

/** Asks the Worker for a page's gist. */
export async function fetchGist(url: string, options: { refresh?: boolean; fetcher?: typeof fetch } = {}): Promise<GistFetchResult> {
  const outcome = await callRoute({ url, ...(options.refresh ? { refresh: true } : {}) }, options.fetcher)
  if (!outcome.ok) return { status: outcome.status, reason: outcome.reason }
  const { body } = outcome
  if (body.status === 'ready' && body.gist && typeof body.gist.text === 'string') return { status: 'ready', gist: body.gist }
  return { status: body.status === 'failed' ? 'failed' : 'unreadable', reason: body.reason ?? 'unknown' }
}

/** Asks the Worker one question about a page. */
export async function fetchAnswer(url: string, question: string, options: { fetcher?: typeof fetch } = {}): Promise<AnswerFetchResult> {
  const outcome = await callRoute({ url, ask: question }, options.fetcher)
  if (!outcome.ok) return { status: outcome.status, reason: outcome.reason }
  const { body } = outcome
  if (body.status === 'ready' && body.answer && typeof body.answer.text === 'string') return { status: 'ready', answer: body.answer }
  return { status: body.status === 'failed' ? 'failed' : 'unreadable', reason: body.reason ?? 'unknown' }
}

export type ReadableFetchResult =
  | { status: 'readable' }
  | { status: 'unreadable' | 'failed' | 'signed-out'; reason: string }

/** Asks the Worker whether a page can be read at all (no summary is written). */
export async function fetchReadable(url: string, options: { fetcher?: typeof fetch } = {}): Promise<ReadableFetchResult> {
  const outcome = await callRoute({ url, check: true }, options.fetcher)
  if (!outcome.ok) return { status: outcome.status, reason: outcome.reason }
  const { body } = outcome
  if (body.status === 'ready' && body.readable) return { status: 'readable' }
  return { status: body.status === 'failed' ? 'failed' : 'unreadable', reason: body.reason ?? 'unknown' }
}
